import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Input, message, Table, Space, Tooltip, Popconfirm, Modal, Select, Tag, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import maintenanceSchedulesService, { MaintenanceSchedule } from '../services/maintenanceSchedulesService';
import equipmentsService, { Equipment } from '../services/equipmentsService';
import { usersService, ApiUser } from '../services/usersService';
import dayjs from 'dayjs';

const MaintenanceSchedulePage: React.FC = () => {
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newSchedule, setNewSchedule] = useState<any>({
    equipmentId: '',
    scheduledDate: dayjs(),
    type: '',
    status: 'scheduled', // Ajout du status
    technicianId: '',
    notes: ''
  });
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [technicians, setTechnicians] = useState<ApiUser[]>([]);

  useEffect(() => {
    fetchSchedules();
    fetchEquipments();
    fetchTechnicians();
  }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await maintenanceSchedulesService.getAll();
      setSchedules(response.data.data || []);
    } catch (error) {
      message.error('Erreur lors du chargement des planifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipments = async () => {
    try {
      const response = await equipmentsService.getAll();
      setEquipments(response.data.data.equipments); // Correction ici
    } catch (error) {
      console.error('Error loading equipments:', error);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await usersService.getUsers({ role: 'technician', status: 'active' });
      setTechnicians(response.data.users);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const handleDateChange = (date: any) => {
    setSelectedDate(date);
  };

  const handleOpenModal = () => {
    setEditMode(false);
    setEditingId(null);
    setNewSchedule({ equipmentId: '', scheduledDate: dayjs(), type: '', status: 'scheduled', technicianId: '', notes: '' });
    setOpenModal(true);
  };

  const handleEditModal = (schedule: MaintenanceSchedule) => {
    setEditMode(true);
    setEditingId(schedule.id);
    setNewSchedule({
      equipmentId: schedule.equipment_id,
      scheduledDate: dayjs(schedule.scheduled_date),
      type: schedule.type,
      status: schedule.status, // Ajout du status
      technicianId: schedule.technician_id,
      notes: schedule.notes
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleScheduleSubmit = async () => {
    try {
      if (editMode && editingId) {
        await maintenanceSchedulesService.update(editingId, {
          equipment_id: Number(newSchedule.equipmentId),
          scheduled_date: newSchedule.scheduledDate.toISOString(),
          type: newSchedule.type,
          status: newSchedule.status || 'scheduled', // Inclure le status avec valeur par défaut
          technician_id: Number(newSchedule.technicianId),
          notes: newSchedule.notes || ''
        });
        message.success('Planning modifié avec succès');
      } else {
        await maintenanceSchedulesService.create({
          equipment_id: Number(newSchedule.equipmentId),
          scheduled_date: newSchedule.scheduledDate.toISOString(),
          type: newSchedule.type,
          status: 'scheduled', // Statut par défaut pour création
          technician_id: Number(newSchedule.technicianId),
          notes: newSchedule.notes || ''
        });
        message.success('Planning créé avec succès');
      }
      fetchSchedules();
      handleCloseModal();
    } catch (error) {
      message.error('Erreur lors de l\'enregistrement du planning');
    }
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const handleDeleteModal = (id: number) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  };
  const handleDeleteConfirm = async () => {
    if (deleteId) {
      try {
        await maintenanceSchedulesService.remove(deleteId);
        fetchSchedules();
        message.success('Planning supprimé avec succès');
      } catch (error) {
        message.error('Erreur lors de la suppression du planning');
      }
    }
    setDeleteModalOpen(false);
    setDeleteId(null);
  };

  const getSchedulesByDate = () => {
    if (!selectedDate) return schedules; // Retourner tous les schedules si pas de date sélectionnée
    return schedules.filter((schedule: MaintenanceSchedule) =>
      dayjs(schedule.scheduled_date).format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD')
    );
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}><span>Chargement...</span></div>;
  }

  const columns = [
    {
      title: 'Équipement',
      dataIndex: 'equipmentName',
      key: 'equipmentName',
    },
    {
      title: 'Date',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: 'Technicien',
      dataIndex: 'technicianName',
      key: 'technicianName',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        let label = status;
        if (status === 'scheduled') { color = 'blue'; label = 'Planifié'; }
        else if (status === 'in_progress') { color = 'orange'; label = 'En cours'; }
        else if (status === 'completed') { color = 'green'; label = 'Terminé'; }
        else if (status === 'cancelled') { color = 'red'; label = 'Annulé'; }
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: MaintenanceSchedule) => (
        <Space size="small">
          <Tooltip title="Modifier">
            <Button icon={<EditOutlined />} onClick={() => handleEditModal(record)} />
          </Tooltip>
          <Popconfirm title="Supprimer ce planning ?" onConfirm={() => handleDeleteModal(record.id)} okText="Oui" cancelText="Non">
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredSchedules = schedules.filter(s => {
    const search = searchTerm.toLowerCase();
    return (
      (s.equipmentName && s.equipmentName.toLowerCase().includes(search)) ||
      (s.technicianName && s.technicianName.toLowerCase().includes(search)) ||
      (s.type && s.type.toLowerCase().includes(search)) ||
      (s.status && s.status.toLowerCase().includes(search))
    );
  }).filter(s => {
    if (!selectedDate) return true; // Si pas de date sélectionnée, afficher tous
    return dayjs(s.scheduled_date).format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD');
  });

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* En-tête */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        backgroundColor: '#fff',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Gestion des Planifications</h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Gérez vos planifications de maintenance
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={handleOpenModal}
          style={{ height: '40px' }}
        >
          Nouvelle Planification
        </Button>
      </div>

      {/* Filtres */}
      <Card style={{ marginBottom: '16px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Input
              placeholder="Rechercher par équipement, technicien, type ou statut..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} md={8}>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              size="large"
            />
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={filteredSchedules}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* Modal création/édition */}
      <Modal
        title={editMode ? 'Modifier le planning' : 'Nouveau planning de maintenance'}
        open={openModal}
        onCancel={handleCloseModal}
        onOk={handleScheduleSubmit}
        okText={editMode ? 'Enregistrer' : 'Planifier'}
        cancelText="Annuler"
      >
        <div style={{ paddingTop: 8 }}>
          <Select
            placeholder="Équipement"
            value={newSchedule.equipmentId}
            onChange={value => setNewSchedule({ ...newSchedule, equipmentId: value })}
            style={{ width: '100%', marginBottom: 16 }}
            size="large"
          >
            {equipments.map((equipment) => (
              <Select.Option key={equipment.id} value={equipment.id}>{equipment.name}</Select.Option>
            ))}
          </Select>
          <DatePicker
            value={newSchedule.scheduledDate}
            onChange={date => setNewSchedule({ ...newSchedule, scheduledDate: date })}
            format="DD/MM/YYYY"
            style={{ width: '100%', marginBottom: 16 }}
            size="large"
          />
          <Select
            placeholder="Type de maintenance"
            value={newSchedule.type}
            onChange={value => setNewSchedule({ ...newSchedule, type: value })}
            style={{ width: '100%', marginBottom: 16 }}
            size="large"
          >
            <Select.Option value="preventive">Préventive</Select.Option>
            <Select.Option value="corrective">Corrective</Select.Option>
            <Select.Option value="predictive">Prédictive</Select.Option>
          </Select>
          <Select
            placeholder="Technicien"
            value={newSchedule.technicianId}
            onChange={value => setNewSchedule({ ...newSchedule, technicianId: value })}
            style={{ width: '100%', marginBottom: 16 }}
            size="large"
          >
            {technicians.map((technician) => (
              <Select.Option key={technician.id} value={technician.id}>
                {(technician.first_name || technician.firstName) + ' ' + (technician.last_name || technician.lastName)}
              </Select.Option>
            ))}
          </Select>
          <Input.TextArea
            placeholder="Notes"
            value={newSchedule.notes || ''}
            onChange={e => setNewSchedule({ ...newSchedule, notes: e.target.value })}
            rows={2}
            style={{ marginBottom: 8 }}
          />
        </div>
      </Modal>

      {/* Modal suppression */}
      <Modal
        title="Confirmer la suppression"
        open={deleteModalOpen}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Supprimer"
        cancelText="Annuler"
        okButtonProps={{ danger: true }}
      >
        Voulez-vous vraiment supprimer ce planning de maintenance ? Cette action est irréversible.
      </Modal>
    </div>
  );
};

export default MaintenanceSchedulePage;